/**
 * ログイン・権限の設定（W22・W23・DEC-4）。
 *
 * この面が守っていること:
 * - **置いてあるのは、口のあるものだけ。** run / stop の設定と、メンバーの
 *   一覧・役割の変更・除名・役割の表である。**招待の口はまだ無い**ので、
 *   枠を置かずに言葉で 1 度だけ書く（枠だけ置くと「誰も招待していない」と
 *   読める空の表ができる）
 * - **押せるかどうかを画面が決めない。** `editableByViewer` はサーバーの
 *   `decide()` の答えそのもので、画面は役割を見て分岐しない
 * - **本人のパスキーもここに置く。** テナントに依らない本人の設定だが、ログインの
 *   手段なので「ログイン・権限」に並べる（run / stop の Card の下。設計 9 節）
 * - **読んだ版をそのまま書き戻す。** `expectedVersion` は画面が数え直さず、
 *   問い合わせが返した `version` を渡す。食い違えば API が `CONFLICT` で断り、
 *   その断りをそのまま出す
 */

import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  PasskeysPanel,
  RunStopPolicyDocument,
  SetRunStopPolicyDocument,
  Tag,
  type RunStopPolicyValue,
} from "visumo-data-agent-web-shared";

import {
  RUN_STOP_POLICY_LABEL,
  RUN_STOP_POLICY_NOTE,
} from "../api-capabilities";
import { HelpIcon } from "../help/HelpIcon";
import { NotificationPreferencePanel } from "./NotificationPreferencePanel";
import { MembersPanel } from "./MembersPanel";
import { useViewer } from "../ViewerContext";

/** 切り替えの並び。**サーバーが受ける値だけ**を出す。 */
const VALUES: readonly RunStopPolicyValue[] = [
  "ADMIN_AND_CS_ONLY",
  "EDITOR_ALLOWED",
];

function formatDateTime(value: string | null | undefined): string | null {
  if (value == null) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
}

export function SettingsPage() {
  const viewer = useViewer();
  const [result, refetch] = useQuery({
    query: RunStopPolicyDocument,
    requestPolicy: "cache-and-network" as const,
  });
  const [saveResult, setRunStopPolicy] = useMutation(SetRunStopPolicyDocument);
  const [saving, setSaving] = useState<RunStopPolicyValue | null>(null);

  const policy = result.data?.runStopPolicy ?? null;
  const userErrors = saveResult.data?.setRunStopPolicy.userErrors ?? [];

  async function choose(value: RunStopPolicyValue): Promise<void> {
    if (policy === null || value === policy.value) return;
    setSaving(value);
    await setRunStopPolicy({
      input: {
        value,
        // 画面が数え直さず、読んだ版をそのまま渡す
        expectedVersion: policy.version,
      },
    });
    setSaving(null);
    // 断られたときも、いまの値を読み直してから出す
    refetch({ requestPolicy: "network-only" });
  }

  const updatedAt = formatDateTime(policy?.updatedAt);

  return (
    <div className="vd-stack">
      <PageHeader
        title="ログイン・権限"
        description="この会社のメンバーが、どこまで操作できるかの設定です。"
        titleExtra={<HelpIcon slug="settings/run-stop" />}
      />

      {result.error !== undefined ? (
        <ErrorState
          title="設定を読み込めませんでした"
          description={result.error.message}
          onRetry={() => refetch({ requestPolicy: "network-only" })}
        />
      ) : policy === null ? (
        <LoadingState label="設定を読み込んでいます" />
      ) : (
        <Card
          title="配信の開始・停止を押せる人"
          note={RUN_STOP_POLICY_NOTE}
          extra={
            <span className="vd-row">
              {policy.editableByViewer ? null : <Tag>変えられません</Tag>}
              <HelpIcon slug="settings/run-stop" />
            </span>
          }
        >
          <div className="vd-stack">
            {policy.editableByViewer ? null : (
              <p className="vd-stat-note" style={{ marginTop: 0 }}>
                この設定を変えられるのは、この会社の管理者だけです。
              </p>
            )}

            {userErrors.length === 0 ? null : (
              <div className="vd-state vd-state--error" role="alert">
                <span className="vd-state-title">
                  設定を変えられませんでした
                </span>
                {userErrors.map((error) => (
                  <span key={`${error.code}-${error.message}`}>
                    {error.message}
                  </span>
                ))}
              </div>
            )}

            <div role="radiogroup" aria-label="配信の開始・停止を押せる人">
              {VALUES.map((value) => {
                const selected = policy.value === value;
                return (
                  <div key={value} style={{ marginBottom: 8 }}>
                    <Button
                      variant={selected ? "primary" : "default"}
                      role="radio"
                      aria-checked={selected}
                      disabled={
                        !policy.editableByViewer ||
                        selected ||
                        saving !== null ||
                        saveResult.fetching
                      }
                      onClick={() => {
                        void choose(value);
                      }}
                    >
                      {saving === value
                        ? "変えています"
                        : RUN_STOP_POLICY_LABEL[value]}
                    </Button>
                  </div>
                );
              })}
            </div>

            <p className="vd-stat-note" style={{ marginBottom: 0 }}>
              {updatedAt === null
                ? "この会社ではまだ 1 度も変えていません（初期値のままです）。"
                : `最後に変えたのは ${updatedAt}${
                    policy.updatedBy === null
                      ? "（変えた人の記録は残っていますが、名前を引けませんでした）"
                      : `・${policy.updatedBy.displayName}`
                  }です。`}
            </p>
          </div>
        </Card>
      )}

      {/* 本人のパスキー（テナントに依らない）。代行中は操作を置かず理由を出す */}
      <PasskeysPanel
        delegated={viewer.delegation != null}
        help={<HelpIcon slug="settings/passkeys" />}
      />
      <NotificationPreferencePanel />
      <MembersPanel />
    </div>
  );
}
